import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BellRing, Sparkles } from 'lucide-react';
import NotifyMeDialog from './NotifyMeDialog';

const BCM95_ID = '94056720-f372-4eef-bcfe-74063137a6e5';

const CATEGORIES = [
  {
    slug: 'mobility-joints',
    title: 'Mobility & Joints',
    product: 'BCM-95®',
    copy: 'Keep moving freely with clinically studied botanical support.',
    image: '/categories/mobility-joints.jpg',
    status: 'available',
    action: 'shop',
  },
  {
    slug: 'stress-calm',
    title: 'Stress & Calm',
    product: 'Somin-On™',
    copy: 'Find your balance in a busy world.',
    image: '/categories/stress-calm.jpg',
    status: 'coming_soon',
    action: 'somin',
  },
  {
    slug: 'energy-calm',
    title: 'Energy & Calm',
    product: 'EnerCalm™',
    copy: 'Energy, focus and calm for active, busy lives — powered by Oxystorm®.',
    image: '/categories/stress-calm.jpg',
    status: 'coming_soon',
    action: 'view',
    page: '/enercalm',
  },
  {
    slug: 'weight-management',
    title: 'Weight Management',
    product: 'PhytoTrim™',
    copy: 'A botanical blend to complement a calorie-controlled diet and an active lifestyle.',
    image: '/categories/weight-management.jpg',
    status: 'coming_soon',
    action: 'view',
    page: '/livslim-xl',
  },
  {
    slug: 'blood-sugar',
    title: 'Blood Sugar Level Management',
    product: 'Glycobalance™',
    copy: 'Everyday metabolic support as part of a balanced, active lifestyle.',
    image: '/categories/blood-sugar.jpg',
    status: 'coming_soon',
    action: 'view',
    page: '/gluzo-eaze',
  },
  {
    slug: 'heart-cholesterol',
    title: 'Heart & Cholesterol',
    product: 'Tri-Low™',
    copy: 'A traditional botanical blend for heart-healthy, active living.',
    image: '/categories/heart-cholesterol.jpg',
    status: 'coming_soon',
    action: 'view',
    page: '/tri-low',
  },
  {
    slug: 'mens-health',
    title: "Men's Health",
    copy: 'Vitality and endurance for an active life.',
    image: '/categories/mens-health.jpg',
    status: 'coming_soon',
    action: 'notify',
  },
  {
    slug: 'womens-health',
    title: "Women's Health",
    copy: "Navigating life's changes with strength and grace.",
    image: '/categories/womens-health.jpg',
    status: 'coming_soon',
    action: 'notify',
  },
];

export const ShopByGoal = () => {
  const navigate = useNavigate();
  const [notify, setNotify] = useState({ open: false, title: '', slug: '' });

  const handleAction = (cat) => {
    if (cat.action === 'shop') navigate(`/products/${BCM95_ID}`);
    else if (cat.action === 'somin') navigate('/somin-on');
    else if (cat.page) navigate(cat.page);
    else setNotify({ open: true, title: cat.title, slug: cat.slug });
  };

  return (
    <section id="shop-by-goal" className="py-24 md:py-32 bg-stone-50" data-testid="shop-by-goal-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl mb-14">
          <span className="text-sm font-medium tracking-widest uppercase text-emerald-700 mb-3 block">Science-backed supplements for every stage of life</span>
          <h2 className="text-4xl md:text-5xl font-bold font-heading text-emerald-950 mb-4 leading-tight">
            One brand, for every stage of life.
          </h2>
          <p className="text-lg text-stone-600">
            We&apos;re building a range of science-backed, nature-derived formulations &mdash; each made for a different part of an active, healthy life. Explore what&apos;s here and what&apos;s on the way.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORIES.map((cat, i) => {
            const soon = cat.status === 'coming_soon';
            return (
              <motion.div
                key={cat.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className="group bg-white rounded-2xl overflow-hidden border border-stone-200 shadow-soft hover:shadow-xl transition-shadow duration-300 flex flex-col"
                data-testid={`category-card-${cat.slug}`}
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={cat.image}
                    alt={cat.title}
                    loading="lazy"
                    className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${soon ? 'opacity-90' : ''}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/40 via-transparent to-transparent" />
                  <div className="absolute top-3 left-3">
                    {soon ? (
                      <span className="inline-flex items-center gap-1.5 bg-amber-500 text-emerald-950 text-[10px] font-bold tracking-wide uppercase px-3 py-1.5 rounded-full shadow">
                        <Sparkles className="w-3 h-3" /> Coming Soon
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-[10px] font-bold tracking-wide uppercase px-3 py-1.5 rounded-full shadow">
                        Available Now
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-baseline justify-between gap-2 mb-2">
                    <h3 className="text-xl font-heading font-semibold text-emerald-950">{cat.title}</h3>
                    {cat.product && <span className="text-xs font-medium text-emerald-700 whitespace-nowrap">{cat.product}</span>}
                  </div>
                  <p className="text-sm text-stone-600 leading-relaxed mb-5 flex-1">{cat.copy}</p>
                  <button
                    onClick={() => handleAction(cat)}
                    className={`inline-flex items-center justify-center gap-2 w-full font-semibold py-3 rounded-xl transition-colors ${
                      soon
                        ? 'bg-stone-100 hover:bg-amber-50 text-emerald-900 border border-stone-200'
                        : 'bg-emerald-800 hover:bg-emerald-900 text-white'
                    }`}
                    data-testid={`category-action-${cat.slug}`}
                  >
                    {cat.action === 'shop' && <>Shop Now <ArrowRight className="w-4 h-4" /></>}
                    {cat.action === 'somin' && <>View Somin-On™ <ArrowRight className="w-4 h-4" /></>}
                    {cat.action === 'view' && <>Learn More <ArrowRight className="w-4 h-4" /></>}
                    {cat.action === 'notify' && <><BellRing className="w-4 h-4" /> Notify Me</>}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <NotifyMeDialog
        open={notify.open}
        onOpenChange={(v) => setNotify((s) => ({ ...s, open: v }))}
        categoryTitle={notify.title}
        productId={`category-${notify.slug}`}
        productName={notify.title}
      />
    </section>
  );
};

export default ShopByGoal;
